// CONSTANTS
import * as roles from '@/constants/roles';
import {
  PERMISSION_MANAGE_PARTNER_SETTINGS,
  PERMISSION_VIEW_MYBUNDLE_USERS,
  PERMISSION_VIEW_SR_OVERVIEW,
  PERMISSION_VIEW_SR_INVITED_USERS,
  PERMISSION_VIEW_SR_TRANSACTIONS_REPORTS,
} from '@/constants/permissions';

import {
  ROUTE_OVERVIEW,
  ROUTE_WP_PAGE,
  ROUTE_REPORTS,
  ROUTE_USERS,
  ROUTE_PROFILES,
  ROUTE_SR_REPORTS,
  ROUTE_SR_OVERVIEW,
  ROUTE_SR_USERS,
  ROUTE_WEBHOOKS,
  ROUTE_STREAMING_ADVICE,
  ROUTE_SETTINGS,
  ROUTE_SR_TRANSACTION_OVERVIEW,
} from './routes';
import { getPartnerAssetsRoute } from './common';

// Main navigation
export const mainNav = [
  { label: 'common.menu.users', route: ROUTE_USERS, icon: 'people' },
  { label: 'common.menu.profiles', route: ROUTE_PROFILES, icon: 'people' },
  {
    label: 'common.menu.srreports',
    route: ROUTE_SR_REPORTS,
    icon: 'credit_card',
    submenu: [
      {
        label: 'common.menu.srOverview',
        route: ROUTE_SR_OVERVIEW,
      },
      {
        label: 'common.menu.srUsers',
        route: ROUTE_SR_USERS,
      },
      {
        label: 'common.menu.transaction',
        route: ROUTE_SR_TRANSACTION_OVERVIEW,
        submenu: [
          {
            label: 'common.menu.transactionOverview',
            route: ROUTE_SR_TRANSACTION_OVERVIEW,
          },
        ],
        openByDefault: true,
      },
    ],
    openByDefault: true,
  },
];

// Partner navigation
export const partnerNav = [
  { label: 'common.menu.overview', route: ROUTE_OVERVIEW, icon: 'assessment' },
  {
    label: 'common.menu.partnerAssets',
    route: ROUTE_WP_PAGE,
    icon: 'description',
  },
  { label: 'common.menu.webhooks', route: ROUTE_WEBHOOKS, icon: 'webhook' },
  {
    label: 'common.menu.srreports',
    route: ROUTE_SR_REPORTS,
    icon: 'credit_card',
    submenu: [
      {
        label: 'common.menu.srOverview',
        route: ROUTE_SR_OVERVIEW,
      },
      {
        label: 'common.menu.srUsers',
        route: ROUTE_SR_USERS,
      },
      {
        label: 'common.menu.transaction',
        route: ROUTE_SR_TRANSACTION_OVERVIEW,
        submenu: [
          {
            label: 'common.menu.transactionOverview',
            route: ROUTE_SR_TRANSACTION_OVERVIEW,
          },
        ],
        openByDefault: true,
      },
    ],
    openByDefault: true,
  },
  {
    label: 'common.menu.reports',
    route: ROUTE_REPORTS,
    icon: 'assessment',
    submenu: [],
    openByDefault: true,
  },
  { label: 'common.menu.users', route: ROUTE_USERS, icon: 'people' },
  { label: 'common.menu.profiles', route: ROUTE_PROFILES, icon: 'people' },
  // Temporary hide settings route from navigation until page is overhauled
  // { label: 'common.menu.settings', route: ROUTE_SETTINGS, icon: 'settings' },
];

// CSR Agent navigation
export const csrAgentNav = [
  { label: 'common.menu.streamingAdvice', route: ROUTE_STREAMING_ADVICE, icon: 'live_tv' },
  { label: 'common.menu.webhooks', route: ROUTE_WEBHOOKS, icon: 'webhook' },
  {
    label: 'common.menu.srreports',
    route: ROUTE_SR_REPORTS,
    icon: 'credit_card',
    submenu: [
      {
        label: 'common.menu.srOverview',
        route: ROUTE_SR_OVERVIEW,
      },
      {
        label: 'common.menu.srUsers',
        route: ROUTE_SR_USERS,
      },
      {
        label: 'common.menu.transaction',
        route: ROUTE_SR_TRANSACTION_OVERVIEW,
        submenu: [
          {
            label: 'common.menu.transactionOverview',
            route: ROUTE_SR_TRANSACTION_OVERVIEW,
          },
        ],
        openByDefault: true,
      },
    ],
    openByDefault: true,
  },
  { label: 'common.menu.profiles', route: ROUTE_PROFILES, icon: 'people' },
];

// CSR View Only navigation
export const getCsrViewOnlyNav = (partner = {}) => [
  {
    label: 'common.menu.partnerAssets',
    route: getPartnerAssetsRoute(partner),
    icon: 'description',
  },
];

function checkSRWebhooksAccessBySettings(settings, route) {
  return route === ROUTE_WEBHOOKS ? settings?.pd_view_sr_webhooks : true;
}

function checkSRReportsAccessBySettings(settings, route) {
  return route === ROUTE_SR_REPORTS ? settings?.pd_view_sr_reports : true;
}

function checkPartnerSettingsAccessByPermissions(permissions, route) {
  return route === ROUTE_SETTINGS
    ? permissions?.includes(PERMISSION_MANAGE_PARTNER_SETTINGS)
    : true;
}

function checkSROverviewAccessByPermissions(permissions, route) {
  return route === ROUTE_SR_OVERVIEW ? permissions?.includes(PERMISSION_VIEW_SR_OVERVIEW) : true;
}

function checkSRInvitedUsersAccessByPermissions(permissions, route) {
  return route === ROUTE_SR_USERS ? permissions?.includes(PERMISSION_VIEW_SR_INVITED_USERS) : true;
}

function checkSRTransactionReportsAccessByPermissions(permissions, route) {
  return route === ROUTE_SR_TRANSACTION_OVERVIEW
    ? permissions?.includes(PERMISSION_VIEW_SR_TRANSACTIONS_REPORTS)
    : true;
}

function checkProfilesAccessByPermissions(permissions, route) {
  return route === ROUTE_PROFILES ? permissions?.includes(PERMISSION_VIEW_MYBUNDLE_USERS) : true;
}

function csrAgentNavFilter({ settings }, { permissions }) {
  return item => {
    if (item.submenu) {
      item.submenu = item.submenu.filter(subItem => {
        if (
          !checkSRInvitedUsersAccessByPermissions(permissions, subItem.route) ||
          !checkSRTransactionReportsAccessByPermissions(permissions, subItem.route) ||
          !checkSROverviewAccessByPermissions(permissions, subItem.route)
        ) {
          return false;
        }

        return subItem;
      });
    }

    if (
      !(
        checkSRWebhooksAccessBySettings(settings, item.route) &&
        checkSRReportsAccessBySettings(settings, item.route) &&
        checkProfilesAccessByPermissions(permissions, item.route)
      )
    ) {
      return false;
    }

    return item;
  };
}

function partnerNavFilter({ settings }, { permissions }) {
  return item => {
    if (item.submenu) {
      item.submenu = item.submenu.filter(subItem => {
        if (
          !checkSRInvitedUsersAccessByPermissions(permissions, subItem.route) ||
          !checkSRTransactionReportsAccessByPermissions(permissions, subItem.route) ||
          !checkSROverviewAccessByPermissions(permissions, subItem.route)
        ) {
          return false;
        }

        return subItem;
      });
    }

    if (
      !(
        checkSRWebhooksAccessBySettings(settings, item.route) &&
        checkSRReportsAccessBySettings(settings, item.route) &&
        checkPartnerSettingsAccessByPermissions(permissions, item.route)
      )
    ) {
      return false;
    }

    return item;
  };
}

export function getNav(role, { partner = {}, user = {} } = {}) {
  if (role === roles.ROLE_SERVICE_ADMIN) {
    return mainNav;
  }

  if (role === roles.ROLE_CSR_AGENT) {
    return csrAgentNav.filter(csrAgentNavFilter(partner, user));
  }

  if (role === roles.ROLE_CSR_VIEW_ONLY) {
    return getCsrViewOnlyNav(partner);
  }

  if (role === roles.ROLE_PARTNER_ADMIN) {
    return partnerNav.filter(partnerNavFilter(partner, user));
  }

  return [];
}

export default mainNav;
